import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { format } from "date-fns";
import { ClientAttachment, downloadBlob, fetchClientAttachment } from "../../services/clientRecordsService";
import { FileTypeIcon, fileSize } from "./fileDisplay";

type Props = {
  clientId: string;
  files: ClientAttachment[];
  /** The file shown; null closes the viewer. */
  index: number | null;
  onIndexChange: (index: number | null) => void;
};

const isImage = (file: ClientAttachment) => file.mimeType.startsWith("image/");
const isPdf = (file: ClientAttachment) => file.mimeType === "application/pdf";

/**
 * Shows a client's file on the page itself: PDFs and photos inline, Word
 * files as a download. The arrows (or the keyboard) step through the others.
 */
const AttachmentViewer: React.FC<Props> = ({ clientId, files, index, onIndexChange }) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const file = index !== null ? files[index] ?? null : null;

  // Fetched once per file while the viewer is open.
  const cache = useRef(new Map<string, { blob: Blob; url: string }>());
  const [current, setCurrent] = useState<{ blob: Blob; url: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cannotShow, setCannotShow] = useState(false);
  const [zoomed, setZoomed] = useState(false);

  const load = useCallback(async () => {
    if (!file) return;
    setCannotShow(false);
    setZoomed(false);
    setError(null);
    const cached = cache.current.get(file.id);
    if (cached) {
      setCurrent(cached);
      return;
    }
    setCurrent(null);
    setLoading(true);
    try {
      const blob = await fetchClientAttachment(clientId, file);
      const entry = { blob, url: URL.createObjectURL(blob) };
      cache.current.set(file.id, entry);
      setCurrent(entry);
    } catch {
      setError("Could not load this file.");
    }
    setLoading(false);
  }, [clientId, file]);

  useEffect(() => {
    load();
  }, [load]);

  // Closing frees the downloaded copies.
  useEffect(() => {
    const store = cache.current;
    if (index === null) {
      store.forEach((entry) => URL.revokeObjectURL(entry.url));
      store.clear();
      setCurrent(null);
    }
  }, [index]);
  useEffect(() => {
    const store = cache.current;
    return () => store.forEach((entry) => URL.revokeObjectURL(entry.url));
  }, []);

  const step = useCallback(
    (by: number) => {
      if (index === null || files.length < 2) return;
      onIndexChange((index + by + files.length) % files.length);
    },
    [index, files.length, onIndexChange]
  );

  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "ArrowRight") step(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, step]);

  const download = () => {
    if (file && current) downloadBlob(current.blob, file.fileName);
  };

  const noPreview = (message: string) => (
    <Box sx={{ textAlign: "center", p: 4, maxWidth: 360 }}>
      {file && <FileTypeIcon file={file} size={56} />}
      <Typography sx={{ fontWeight: 700, mt: 1.5 }}>{message}</Typography>
      <Typography sx={{ color: "text.secondary", fontSize: "0.88rem", mt: 0.5, mb: 2 }}>
        Download it to open it on this device.
      </Typography>
      <Button variant="contained" disableElevation startIcon={<DownloadIcon />} onClick={download} disabled={!current} sx={{ textTransform: "none" }}>
        Download
      </Button>
    </Box>
  );

  let body: React.ReactNode = null;
  if (file) {
    if (loading) body = <CircularProgress />;
    else if (error)
      body = (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      );
    else if (current && isPdf(file))
      body = <Box component="iframe" title={file.title || file.fileName} src={current.url} sx={{ border: 0, width: "100%", height: "100%", bgcolor: "#fff" }} />;
    else if (current && isImage(file) && !cannotShow)
      body = (
        // Pinned to the panel's edges so "fit" has a real size to fit into.
        <Box sx={{ position: "absolute", inset: 0, overflow: zoomed ? "auto" : "hidden", display: "flex", p: zoomed ? 0 : 2 }}>
          <Box
            component="img"
            src={current.url}
            alt={file.title || file.fileName}
            onError={() => setCannotShow(true)}
            onClick={() => setZoomed((z) => !z)}
            sx={{
              display: "block",
              cursor: zoomed ? "zoom-out" : "zoom-in",
              ...(zoomed
                ? // Real pixels, scrolled when bigger than the screen.
                  { m: "auto", flexShrink: 0, maxWidth: "none" }
                : // The whole photo, as large as the screen allows.
                  { width: "100%", height: "100%", objectFit: "contain" }),
            }}
          />
        </Box>
      );
    else if (current && isImage(file)) body = noPreview("This browser cannot show this photo");
    else if (current) body = noPreview("No preview for this kind of file");
  }

  return (
    <Dialog
      open={Boolean(file)}
      onClose={() => onIndexChange(null)}
      fullScreen={isPhone}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { height: isPhone ? "100%" : "90vh", borderRadius: isPhone ? 0 : 3, overflow: "hidden" } }}
    >
      {file && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.25, borderBottom: "1px solid", borderColor: "divider" }}>
            <FileTypeIcon file={file} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }} noWrap>
                {file.title || file.fileName}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }} noWrap>
                {[file.category, format(new Date(file.createdAt), "d MMM yyyy"), fileSize(file.sizeBytes), file.uploadedByName]
                  .filter(Boolean)
                  .join(" · ")}
                {files.length > 1 && index !== null ? `  ·  ${index + 1} of ${files.length}` : ""}
              </Typography>
            </Box>
            {isImage(file) && !cannotShow && current && (
              <Tooltip title={zoomed ? "Fit to screen" : "Actual size"}>
                <IconButton aria-label={zoomed ? "Fit to screen" : "Actual size"} onClick={() => setZoomed((z) => !z)}>
                  {zoomed ? <ZoomOutIcon /> : <ZoomInIcon />}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Download">
              <span>
                <IconButton aria-label="Download" onClick={download} disabled={!current}>
                  <DownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton aria-label="Close" onClick={() => onIndexChange(null)}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ position: "relative", flexGrow: 1, minHeight: 0, bgcolor: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {body}
            {files.length > 1 && (
              <>
                <IconButton
                  aria-label="Previous file"
                  onClick={() => step(-1)}
                  sx={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", bgcolor: "rgba(255,255,255,0.9)", boxShadow: 1, "&:hover": { bgcolor: "#fff" } }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton
                  aria-label="Next file"
                  onClick={() => step(1)}
                  sx={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", bgcolor: "rgba(255,255,255,0.9)", boxShadow: 1, "&:hover": { bgcolor: "#fff" } }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </>
            )}
          </Box>
        </>
      )}
    </Dialog>
  );
};

export default AttachmentViewer;
